import { useEffect, useMemo, useState } from 'react';
import { PixelRatio } from 'react-native';
import { getCloudinaryUrl, getContentfulImageUrl } from '@sg/garnish';

import type { ContentfulAsset } from '@order/Contentful';
import { useContentfulClient } from '@order/Contentful';
import { useLocalizationContext } from '@order/Localization';

export const useHomeScreenLoadedContent = (): HomeScreenLoadedContent => {
  const [content, setContent] = useState<HomeScreenLoadedContent>(
    useHomeScreenDefaultContent(),
  );

  const contentfulClient = useContentfulClient();

  useEffect(() => {
    if (!contentfulClient) return;

    void contentfulClient
      .getEntries<ContentfulHomeScreenFields>({
        content_type: 'homeScreen',
        limit: 1,
        order: '-sys.updatedAt',
      })
      .then((entries) => {
        const loadedContent = contentFromEntry(entries.items[0]?.fields);

        if (!loadedContent) return;

        setContent(loadedContent);
      });
  }, [contentfulClient]);

  return content;
};

const useHomeScreenDefaultContent = (): HomeScreenLoadedContent => {
  const { t } = useLocalizationContext();

  return useMemo(
    () => ({
      heroHeadlineText: t('home.welcome.evergreen'),
      heroImageURL: getCloudinaryUrl(
        'https://res.cloudinary.com/sweetgreen/image/upload/v1667846265/210717_sweetgreen_sg_on_the_go_v9_5653_hdeqbz.jpg',
        { width: 700 },
      ),
      heroCTA: {
        text: t('home.hero-cta'),
        action: 'view_menu',
      },
    }),
    [t],
  );
};

const contentFromEntry = (
  fields?: ContentfulHomeScreenFields,
): HomeScreenLoadedContent | undefined => {
  const imageSize = PixelRatio.get() > 1 ? 1400 : 700;

  return fields
    ? {
        heroHeadlineText: fields.heroHeadlineText,
        heroCTA:
          fields.heroCtaAction !== undefined && fields.heroCtaText !== undefined
            ? {
                text: fields.heroCtaText,
                action: fields.heroCtaAction,
              }
            : undefined,
        heroImageURL: getContentfulImageUrl(fields.heroImage.fields.file.url, {
          fm: 'webp',
          q: 90,
          w: imageSize,
          h: imageSize,
          fit: 'fill',
        }),
      }
    : undefined;
};

type HomeScreenLoadedContent = {
  readonly heroHeadlineText: string;
  readonly heroImageURL?: string;
  readonly heroCTA?: {
    readonly text: string;
    readonly action: string;
  };
};

type ContentfulHomeScreenFields = {
  readonly heroHeadlineText: string;
  readonly heroImage: ContentfulAsset;
  readonly heroCtaAction?: string;
  readonly heroCtaText?: string;
};
